import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container, Box, Typography, Button, TextField,
    Card, CardContent, CircularProgress, LinearProgress,
    Chip, useTheme
} from '@mui/material';
import {
    KeyboardArrowLeft, Save, Image as ImageIcon,
    VideoLibrary, Delete
} from '@mui/icons-material';
import AdminHeader from '../components/AdminHeader';
import blogApi from '../services/blogApi';

const BlogEditor = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const theme = useTheme();
    const isEdit = Boolean(id);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploading, setUploading] = useState(false);

    const [form, setForm] = useState({
        title: '',
        content: '',
        author: '',
        published: false,
        imageUrl: '',
        videoUrl: ''
    });

    // ===== AKO E EDIT - VCHITAJ POST =====
    useEffect(() => {
        if (isEdit) {
            setLoading(true);
            blogApi.getPostById(id)
                .then(post => {
                    setForm({
                        title: post.title || '',
                        content: post.content || '',
                        author: post.author || '',
                        published: post.published || false,
                        imageUrl: post.imageUrl || '',
                        videoUrl: post.videoUrl || ''
                    });
                })
                .catch(err => console.error('Error loading post:', err))
                .finally(() => setLoading(false));
        }
    }, [id, isEdit]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // ===== UPLOAD SLIKA =====
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        setUploadProgress(0);
        try {
            const res = await blogApi.uploadImage(file, (percent) => {
                setUploadProgress(percent);
            });
            console.log('��️ Upload res:', res); // ← ДОДАЈ ОВА
            console.log('🖼️ imageUrl:', res.imageUrl); // ← И ОВА

            setForm(prev => ({ ...prev, imageUrl: res.imageUrl, videoUrl: '' }));
        } catch (err) {
            console.error('Error uploading image:', err);
            alert('Грешка при качување на слика!');
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // ===== UPLOAD VIDEO =====
    const handleVideoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        setUploadProgress(0);
        try {
            const res = await blogApi.uploadVideo(file, (percent) => {
                setUploadProgress(percent);
            });
            setForm(prev => ({ ...prev, videoUrl: res.videoUrl, imageUrl: '' }));
        } catch (err) {
            console.error('Error uploading video:', err);
            alert('Грешка при качување на видео!');
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // ===== SAVE =====
    const handleSave = async (publish = null) => {
        if (!form.title.trim()) {
            alert('Насловот е задолжителен!');
            return;
        }
        if (!form.content.trim()) {
            alert('Содржината е задолжителна!');
            return;
        }

        setSaving(true);
        try {
            const postData = {
                ...form,
                published: publish !== null ? publish : form.published
            };

            if (isEdit) {
                await blogApi.updatePost(id, postData);
            } else {
                await blogApi.createPost(postData);
            }

            navigate('/admin/blog');
        } catch (err) {
            console.error('Error saving post:', err);
            alert('Грешка при зачувување!');
        } finally {
            setSaving(false);
        }
    };

    // ===== LOADING =====
    if (loading) {
        return (
            <Box sx={{
                minHeight: '100vh',
                backgroundColor: theme.palette.background.default,
                display: 'flex', justifyContent: 'center', alignItems: 'center'
            }}>
                <CircularProgress sx={{ color: '#ff7eb9' }} />
                <Typography variant="h6" sx={{ ml: 2, color: theme.palette.text.primary }}>
                    Вчитување...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ py: 4, minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
            <Container maxWidth="md">
                <AdminHeader />

                {/* Назад копче */}
                <Box sx={{ mb: 4 }}>
                    <Button
                        onClick={() => navigate('/admin/blog')}
                        startIcon={<KeyboardArrowLeft sx={{ fontSize: '28px !important' }} />}
                        variant="outlined"
                        sx={{
                            color: '#ff7eb9', borderColor: '#ff7eb9',
                            backgroundColor: 'rgba(255,126,185,0.06)',
                            fontWeight: 'bold', fontSize: '1rem',
                            px: 3, py: 1.5, borderRadius: '12px',
                            textTransform: 'none', transition: 'all 0.3s ease',
                            '&:hover': {
                                backgroundColor: '#ff7eb9', color: 'white',
                                borderColor: '#ff4a97', transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(255,126,185,0.3)',
                            }
                        }}
                    >
                        Назад кон Блог
                    </Button>
                </Box>

                {/* Наслов */}
                <Typography variant="h3" sx={{
                    fontWeight: 900, mb: 6,
                    background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    fontSize: { xs: '2rem', md: '2.5rem' }
                }}>
                    {isEdit ? '✏️ Уреди Статија' : '➕ Нова Статија'}
                </Typography>

                <Card sx={{
                    borderRadius: '20px',
                    backgroundColor: theme.palette.background.paper,
                    border: '1px solid rgba(255,126,185,0.15)',
                    boxShadow: '0 4px 24px rgba(255,126,185,0.08)'
                }}>
                    <Box sx={{ height: 5, background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)' }} />
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>

                        {/* Наслов */}
                        <TextField
                            fullWidth
                            label="Наслов *"
                            name="title"
                            value={form.title}
                            onChange={handleChange}
                            sx={{ mb: 3 }}
                            InputProps={{ sx: { borderRadius: '12px' } }}
                        />

                        {/* Автор */}
                        <TextField
                            fullWidth
                            label="Автор"
                            name="author"
                            value={form.author}
                            onChange={handleChange}
                            sx={{ mb: 3 }}
                            InputProps={{ sx: { borderRadius: '12px' } }}
                        />

                        {/* Содржина */}
                        <TextField
                            fullWidth
                            multiline
                            rows={12}
                            label="Содржина * (поддржува HTML)"
                            name="content"
                            value={form.content}
                            onChange={handleChange}
                            sx={{ mb: 3 }}
                            InputProps={{ sx: { borderRadius: '12px', fontFamily: 'monospace' } }}
                            placeholder="<p>Напиши ја содржината овде...</p>"
                        />

                        {/* ===== MEDIA UPLOAD ===== */}
                        <Typography variant="h6" sx={{
                            fontWeight: 'bold', mb: 2, color: theme.palette.text.primary
                        }}>
                            📎 Медиа (слика или видео)
                        </Typography>

                        {/* Upload прогрес */}
                        {uploading && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1, color: '#ff7eb9' }}>
                                    Се качува... {uploadProgress}%
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={uploadProgress}
                                    sx={{
                                        borderRadius: '4px', height: 8,
                                        backgroundColor: 'rgba(255,126,185,0.15)',
                                        '& .MuiLinearProgress-bar': {
                                            background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)'
                                        }
                                    }}
                                />
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                            {/* Upload слика */}
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={<ImageIcon />}
                                disabled={uploading}
                                sx={{
                                    borderRadius: '12px', textTransform: 'none',
                                    borderColor: '#ff7eb9', color: '#ff7eb9',
                                    '&:hover': { backgroundColor: 'rgba(255,126,185,0.08)' }
                                }}
                            >
                                Качи Слика
                                <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                            </Button>

                            {/* Upload видео */}
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={<VideoLibrary />}
                                disabled={uploading}
                                sx={{
                                    borderRadius: '12px', textTransform: 'none',
                                    borderColor: '#AB47BC', color: '#AB47BC',
                                    '&:hover': { backgroundColor: 'rgba(171,71,188,0.08)' }
                                }}
                            >
                                Качи Видео
                                <input type="file" hidden accept="video/*" onChange={handleVideoUpload} />
                            </Button>
                        </Box>

                        {/* Preview слика */}
                        {form.imageUrl && (
                            <Box sx={{ mb: 3, position: 'relative', display: 'inline-block' }}>
                                <Chip
                                    label="🖼️ Слика прикачена"
                                    sx={{ bgcolor: 'rgba(255,126,185,0.1)', color: '#ff7eb9', mb: 1 }}
                                />
                                <Box>
                                    <img
                                        src={form.imageUrl}
                                        alt="preview"
                                        style={{
                                            maxWidth: '100%', maxHeight: '200px',
                                            borderRadius: '12px', display: 'block'
                                        }}
                                    />
                                    <Button
                                        size="small"
                                        startIcon={<Delete />}
                                        onClick={() => setForm(prev => ({ ...prev, imageUrl: '' }))}
                                        sx={{ mt: 1, color: '#ff6b6b', textTransform: 'none' }}
                                    >
                                        Тргни слика
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* Preview видео */}
                        {form.videoUrl && (
                            <Box sx={{ mb: 3 }}>
                                <Chip
                                    label="🎬 Видео прикачено"
                                    sx={{ bgcolor: 'rgba(171,71,188,0.1)', color: '#AB47BC', mb: 1 }}
                                />
                                <Box>
                                    <video
                                        src={form.videoUrl}
                                        controls
                                        style={{
                                            maxWidth: '100%', maxHeight: '200px',
                                            borderRadius: '12px', display: 'block'
                                        }}
                                    />
                                    <Button
                                        size="small"
                                        startIcon={<Delete />}
                                        onClick={() => setForm(prev => ({ ...prev, videoUrl: '' }))}
                                        sx={{ mt: 1, color: '#ff6b6b', textTransform: 'none' }}
                                    >
                                        Тргни видео
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* ===== АКЦИИ ===== */}
                        <Box sx={{
                            display: 'flex', gap: 2, flexWrap: 'wrap',
                            pt: 3, borderTop: `1px solid ${theme.palette.divider}`
                        }}>
                            {/* Зачувај како Draft */}
                            <Button
                                variant="outlined"
                                startIcon={<Save />}
                                onClick={() => handleSave(false)}
                                disabled={saving}
                                sx={{
                                    borderRadius: '12px', textTransform: 'none',
                                    fontWeight: 'bold', px: 3, py: 1.5,
                                    borderColor: theme.palette.divider,
                                    color: theme.palette.text.secondary,
                                    '&:hover': { borderColor: '#ff7eb9', color: '#ff7eb9' }
                                }}
                            >
                                {saving ? <CircularProgress size={20} /> : 'Зачувај Draft'}
                            </Button>

                            {/* Зачувај и Објави */}
                            <Button
                                variant="contained"
                                startIcon={<Save />}
                                onClick={() => handleSave(true)}
                                disabled={saving}
                                sx={{
                                    borderRadius: '12px', textTransform: 'none',
                                    fontWeight: 'bold', px: 3, py: 1.5,
                                    background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                    boxShadow: '0 4px 15px rgba(255,126,185,0.3)',
                                    '&:hover': {
                                        background: 'linear-gradient(45deg, #ff4a97, #9C27B0)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 25px rgba(255,126,185,0.4)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : '🚀 Зачувај и Објави'}
                            </Button>
                        </Box>

                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default BlogEditor;